import { ProfileType } from '@/modules/profile/types';
import { PreferencesType } from '@/modules/preferences/types';
import { ProjectSettingsType, ProjectType } from '@/modules/projects/types';
import { MetaDataResponseType } from '@/types/common';
import {
  PlagiarismCheckerItemDetectionType,
  PlagiarismCheckerResponseType,
} from '@/modules/tools/PlagiarismChecker/types';
import { ParaphraserResponseType } from '@/modules/tools/Paraphraser/types';
import { AiDetectorItemDetectionType, AiDetectorResponseType } from '@/modules/tools/AiDetector/types';
import { AiHumanizerResponseType } from '@/modules/tools/AiHumanizer/types';

export const mapMetaResponse = (meta: Record<string, any>): MetaDataResponseType => {
  return {
    current_page: meta.current_page || 1,
    page_size: meta.page_size || 10,
    total_count: meta.total_count || 1,
    total_pages: meta.total_pages || 1,
  };
};

export const mapProfileResponse = (profile: Record<string, any>): ProfileType => {
  return {
    id: profile.id || '',
    email: profile.email || '',
    name: profile.name || '',
    avatar: profile.avatar || '',
  };
};

export const mapPreferencesResponse = (preferences: Record<string, any>): PreferencesType => {
  return {
    language: preferences?.language || '',
    theme: preferences?.theme || 'system',
  };
};

export const mapProjectResponse = (project: Record<string, any>): ProjectType => {
  return {
    id: project.id || '',
    user_title: project.user_title || '',
    system_title: project.system_title || '',
    status: project.status || '',
    created_at: project.created_at || '',
    updated_at: project.updated_at || '',
  };
};

export const mapProjectSettingsResponse = (settings: Record<string, any>): ProjectSettingsType => {
  return {
    counter_unit: settings.counter_unit ?? null,
  };
};

export const mapPlagiarismCheckerItemDetectionResponse = (
  detection: Record<string, any>,
): PlagiarismCheckerItemDetectionType => {
  return {
    id: detection.id || '',
    text: detection.text || '',
    status: detection.status || '',
    part_of_source: detection.part_of_source || 0,
    reference: {
      authors: detection.reference?.authors || [],
      source: detection.reference?.source || '',
      url: detection.reference?.url || '',
      published_at: detection.reference?.published_at || '',
    },
    created_at: detection.created_at || '',
    updated_at: detection.updated_at || '',
  };
};

export const mapPlagiarismCheckerResponse = (
  plagiarism_checker: Record<string, any>,
): PlagiarismCheckerResponseType => {
  return {
    id: plagiarism_checker.id || '',
    project_id: plagiarism_checker.project_id || '',
    score: plagiarism_checker.score || 0,
    detections: (plagiarism_checker.detections || []).map(mapPlagiarismCheckerItemDetectionResponse),
    created_at: plagiarism_checker.created_at || '',
    updated_at: plagiarism_checker.updated_at || '',
  };
};

export const mapParaphraserResponse = (paraphraser: Record<string, any>): ParaphraserResponseType => {
  return {
    id: paraphraser.id || '',
    project_id: paraphraser.project_id || '',
    original_text: paraphraser.original_text || '',
    paraphrased_text: paraphraser.paraphrased_text || '',
    status: paraphraser.status || '',
    created_at: paraphraser.created_at || '',
    updated_at: paraphraser.updated_at || '',
  };
};

export const mapAiDetectorDetectionResponse = (detection: Record<string, any>): AiDetectorItemDetectionType => {
  return {
    id: detection.id || '',
    text: detection.text || '',
    status: detection.status || '',
    created_at: detection.created_at || '',
    updated_at: detection.updated_at || '',
  };
};

export const mapAiDetectorResponse = (ai_detector: Record<string, any>): AiDetectorResponseType => {
  return {
    id: ai_detector.id || '',
    project_id: ai_detector.project_id || '',
    score: ai_detector.score || 0,
    detections: (ai_detector.detections || []).map(mapAiDetectorDetectionResponse),
    created_at: ai_detector.created_at || '',
    updated_at: ai_detector.updated_at || '',
  };
};

export const mapAiHumanizerResponse = (ai_humanizer: Record<string, any>): AiHumanizerResponseType => {
  return {
    id: ai_humanizer.id || '',
    project_id: ai_humanizer.project_id || '',
    original_text: ai_humanizer.original_text || '',
    humanized_text: ai_humanizer.humanized_text || '',
    status: ai_humanizer.status || '',
    created_at: ai_humanizer.created_at || '',
    updated_at: ai_humanizer.updated_at || '',
  };
};
